/**
 * Unified Session Service
 * Consolidates all session management into a single, optimized system
 * Maintains all security features while eliminating conflicts
 */

import { supabase } from '@/integrations/supabase/client';
import { Session } from '@supabase/supabase-js';
import { logSecurityEvent } from '@/utils/security';
import { securityConfig } from '@/config/security';

export class UnifiedSessionService {
  private static instance: UnifiedSessionService | null = null;
  private static readonly SESSION_TIMEOUT_MS = securityConfig.inactivityTimeoutMinutes * 60 * 1000; // from config
  private static readonly SECURITY_CHECK_INTERVAL = 10 * 60 * 1000; // 10 minutes

  private lastSecurityCheck = 0;
  private sessionFingerprint: string | null = null;
  private authSubscription: { unsubscribe: () => void } | null = null;
  
  private constructor() {}
  
  public static getInstance(): UnifiedSessionService {
    if (!UnifiedSessionService.instance) {
      UnifiedSessionService.instance = new UnifiedSessionService();
    }
    return UnifiedSessionService.instance;
  }
  
  /**
   * Initialize unified session monitoring
   */
  public initializeSession(
    onSignIn?: (session: Session) => void,
    onSignOut?: () => void
  ): { unsubscribe: () => void } {
    // Clean up any existing subscription
    if (this.authSubscription) {
      this.authSubscription.unsubscribe();
    }
    
    // Set up single auth state listener
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      console.log('Unified auth state change:', event);
      
      switch (event) {
        case 'SIGNED_IN':
          if (session) {
            this.handleSignIn(session);
            onSignIn?.(session);
          }
          break;
          
        case 'SIGNED_OUT':
          this.handleSignOut();
          onSignOut?.();
          break;
          
        case 'TOKEN_REFRESHED':
          if (session) {
            this.updateLastActivity();
            console.log('Token refreshed at:', new Date().toISOString());
          }
          break;
      }
    });
    
    this.authSubscription = subscription;
    
    return {
      unsubscribe: () => {
        subscription.unsubscribe();
        this.authSubscription = null;
      }
    };
  }
  
  /**
   * Handle sign in with security validation
   */
  private handleSignIn(session: Session): void {
    // Validate session integrity
    if (!this.validateSession(session)) {
      logSecurityEvent('invalid_session_detected', { event: 'SIGNED_IN' });
      // Relaxed: do not force sign-out; rely on Supabase token refresh
    }
    
    // Generate and store session fingerprint
    this.sessionFingerprint = this.generateSessionFingerprint();
    sessionStorage.setItem('session_fingerprint', this.sessionFingerprint);
    sessionStorage.setItem('current_user_id', session.user.id);
    
    // Update activity
    this.updateLastActivity();
    
    logSecurityEvent('session_established', { userId: session.user.id });
  }
  
  /**
   * Handle sign out with cleanup
   */
  private handleSignOut(): void {
    this.clearAllSessionData();
    logSecurityEvent('session_terminated');
  }
  
  /**
   * Update last activity (single source of truth)
   */
  public updateLastActivity(): void {
    try {
      const now = new Date().toISOString();
      
      // Update timestamp BEFORE performing security checks
      sessionStorage.setItem('last_activity', now);
      
      // Only perform security checks at intervals to prevent spam
      this.performScheduledSecurityCheck();
    } catch (error) {
      console.warn('Could not update last activity:', error);
    }
  }
  
  /**
   * Perform security checks only when needed
   */
  private performScheduledSecurityCheck(): void {
    const now = Date.now();
    const timeSinceLastCheck = now - this.lastSecurityCheck;
    
    // Only check every 10 minutes to reduce overhead
    if (timeSinceLastCheck < UnifiedSessionService.SECURITY_CHECK_INTERVAL) {
      return;
    }
    
    this.lastSecurityCheck = now;
    
    // Check for session timeout
    if (this.checkSessionTimeout()) {
      logSecurityEvent('auto_logout_session_timeout');
      supabase.auth.signOut();
      return;
    }
    
    // Check for suspicious activity
    if (this.detectSuspiciousActivity()) {
      logSecurityEvent('suspicious_activity_detected');
      supabase.auth.signOut();
      return;
    }
  }
  
  /**
   * Check if session has timed out
   */
  private checkSessionTimeout(): boolean {
    try {
      const stored = sessionStorage.getItem('last_activity');
      if (!stored) return false;
      
      const lastActivity = new Date(stored);
      const now = new Date();
      const timeSinceLastActivity = now.getTime() - lastActivity.getTime();
      
      if (timeSinceLastActivity > UnifiedSessionService.SESSION_TIMEOUT_MS) {
        logSecurityEvent('session_timeout', {
          lastActivity: lastActivity.toISOString(),
          timeSinceLastActivity,
          maxInactiveMs: UnifiedSessionService.SESSION_TIMEOUT_MS
        });
        return true;
      }
      
      return false;
    } catch (error) {
      console.error('Error checking session timeout:', error);
      return false;
    }
  }
  
  /**
   * Detect suspicious session activity
   */
  private detectSuspiciousActivity(): boolean {
    try {
      const currentUserId = sessionStorage.getItem('current_user_id');
      const storedFingerprint = sessionStorage.getItem('session_fingerprint');
      
      if (!currentUserId || !storedFingerprint) return false;
      
      // Check fingerprint mismatch (could indicate session hijacking)
      const currentFingerprint = this.generateSessionFingerprint();
      if (storedFingerprint !== currentFingerprint) {
        logSecurityEvent('session_fingerprint_mismatch', {
          storedFingerprint: storedFingerprint.slice(0, 8) + '...',
          currentFingerprint: currentFingerprint.slice(0, 8) + '...',
          suspiciousActivity: true
        });
        return securityConfig.enforceFingerprint;
      }
      
      return false;
    } catch (error) {
      console.error('Error detecting suspicious activity:', error);
      return false;
    }
  }
  
  /**
   * Validate session integrity
   */
  private validateSession(session: Session): boolean {
    if (!session || !session.access_token || !session.expires_at) {
      return false;
    }
    
    const expiresAt = new Date(session.expires_at * 1000);
    const now = new Date();
    const fiveMinutesFromNow = new Date(now.getTime() + (5 * 60 * 1000));
    
    // If expiring soon, warn but allow; Supabase auto-refresh handles renewal
    if (expiresAt <= fiveMinutesFromNow) {
      logSecurityEvent('session_expiring_soon', { 
        expiresAt: expiresAt.toISOString(),
        timeUntilExpiry: expiresAt.getTime() - now.getTime()
      });
      return true;
    }
    
    return true;
  }
  
  /**
   * Generate secure session fingerprint
   */
  private generateSessionFingerprint(): string {
    try {
      const fingerprint = {
        userAgent: navigator.userAgent,
        language: navigator.language,
        platform: navigator.platform,
        timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
        screenResolution: `${screen.width}x${screen.height}`,
        timestamp: Date.now()
      };
      
      return btoa(JSON.stringify(fingerprint)).slice(0, 32);
    } catch (error) {
      console.warn('Could not generate session fingerprint:', error);
      return 'fallback-fingerprint';
    }
  }
  
  /**
   * Clear all session-related data
   */
  private clearAllSessionData(): void {
    try {
      // Clear session storage
      const keysToRemove = [
        'last_activity',
        'current_user_id',
        'session_fingerprint'
      ];
      
      keysToRemove.forEach(key => {
        sessionStorage.removeItem(key);
      });
      
      // Clear profile cache
      Object.keys(sessionStorage).forEach(key => {
        if (key.startsWith('profile-') || key.startsWith('cached-')) {
          sessionStorage.removeItem(key);
        }
      });
      
      // Clear rate limiting data
      Object.keys(localStorage).forEach(key => {
        if (key.startsWith('rate_limit_') || key.startsWith('auth_')) {
          localStorage.removeItem(key);
        }
      });
      
      logSecurityEvent('session_data_cleared');
    } catch (error) {
      console.error('Error clearing session data:', error);
    }
  }
  
  /**
   * Get last activity timestamp
   */
  public getLastActivity(): Date | null {
    try {
      const stored = sessionStorage.getItem('last_activity');
      return stored ? new Date(stored) : null;
    } catch (error) {
      console.warn('Could not retrieve last activity timestamp:', error);
      return null;
    }
  }
  
  /**
   * Get session expiration time based on last activity
   */
  public getSessionExpirationTime(): Date | null {
    const lastActivity = this.getLastActivity();
    if (!lastActivity) return null;
    
    return new Date(lastActivity.getTime() + UnifiedSessionService.SESSION_TIMEOUT_MS);
  }
  
  /**
   * Cleanup and destroy instance
   */
  public destroy(): void {
    if (this.authSubscription) {
      this.authSubscription.unsubscribe();
      this.authSubscription = null;
    }
    UnifiedSessionService.instance = null;
  }
}

// Export singleton instance
export const unifiedSessionService = UnifiedSessionService.getInstance();